import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { AnalyticsService } from '@libs/src/analytics/analytics.service';
import { AccountEditService } from '@main-client/src/app/account/account-edit.service';
import { GeocodeService } from '@main-client/src/app/core/geocode.service';
import { LocationService } from '@main-client/src/app/core/location.service';
import { FormValidatorService } from '@main-client/src/app/form/form-validator.service';

import { ProfileUnsavedChangesDialogComponent } from '@main-client/src/app/profile/profile-unsaved-changes-dialog/profile-unsaved-changes-dialog.component';

import { Account } from '@libs/src/models/account.model';
import { sanitizeUrlWithProtocol } from '@libs/src/utilities/url.utilities';
import { IAppState } from '@main-client/src/app/app.state';
import {
  profileAboutCharLimit,
  profileExperienceEducationCharLimit,
  profilePronounsCharLimit,
  profileShortBioCharLimit,
} from '@main-client/src/app/profile/profile';
import {
  EditedProfile,
  LoadProfile,
} from '@main-client/src/app/profile/profile-store/profile.actions';

import defer from 'lodash-es/defer';
import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import keys from 'lodash-es/keys';
import pick from 'lodash-es/pick';
import result from 'lodash-es/result';
import set from 'lodash-es/set';
import some from 'lodash-es/some';
import tail from 'lodash-es/tail';

interface FormGroupControl extends AbstractControl {
  controls?: { [key: string]: AbstractControl };
}

const ADMIN_EDITABLE_FIELDS = ['email'];
const BASE_EDITABLE_FIELDS = [
  'about',
  'education',
  'experience',
  'first_name',
  'geo_location',
  'last_name',
  'pronouns',
  'short_bio',
  'website',
];
const CHARACTER_LIMIT = profileExperienceEducationCharLimit;
const EDITABLE_FIELDS = [
  'full_name',
  ...BASE_EDITABLE_FIELDS,
  ...ADMIN_EDITABLE_FIELDS,
];
const BASE_SUBFORM_CONFIGS = {
  education: {
    program: '',
    school: '',
    class_of: '',
  },
  experience: {
    organization_name: '',
    job_title: '',
  },
  geo_location: {
    address: '',
    location: {},
  },
};
const profileArrayFields = ['education', 'experience', 'geo_location'];

@Component({
  selector: 'app-profile-intro-edit-dialog',
  templateUrl: './profile-intro-edit-dialog.component.html',
})
export class ProfileIntroEditDialogComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  @ViewChild('inputAbout') inputAbout: ElementRef;
  @ViewChild('inputExperienceJobTitle')
  inputExperienceJobTitle: ElementRef;
  @ViewChild('inputLocation') inputLocation: ElementRef;
  @ViewChild('inputSchoolProgram')
  inputSchoolProgram: ElementRef;
  @ViewChild('inputWebsite') inputWebsite: ElementRef;
  readonly aboutCharacterLimit = profileAboutCharLimit;
  readonly pronounsCharacterLimit = profilePronounsCharLimit;
  readonly shortBioCharLimit = profileShortBioCharLimit;
  readonly CHARACTER_LIMIT = CHARACTER_LIMIT;
  tenantState = this.store.select('tenant');
  user: Account;
  profile: Account;
  isUser: boolean;
  form: UntypedFormGroup;
  filteredLocationOptions: Observable<string[]>;
  initialValue: any;
  isLoading = false;
  isLoadingImage = false;
  error = '';
  showLinkedInAuth = false;
  formChangesSubscription: Subscription;
  componentDestroy = new Subject<void>();
  formHasChanges = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { focusElement?: string; profile: Account; user: Account },
    public accountEditService: AccountEditService,
    public accountBackend: AccountBackend,
    public analyticsService: AnalyticsService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ProfileIntroEditDialogComponent>,
    private fb: UntypedFormBuilder,
    private formValidatorService: FormValidatorService,
    public geocoderService: GeocodeService,
    private locationService: LocationService,
    private store: Store<IAppState>,
  ) {}

  ngOnInit() {
    this.profile = this.data.profile;
    this.user = this.data.user;
    this.isUser = isEqual(this.profile._id, this.user._id);
    const baseFormControls = {
      about: [
        get(this.profile, 'about', ''),
        Validators.compose([Validators.maxLength(this.aboutCharacterLimit)]),
      ],
      education: this.fb.group({
        program: [
          get(this.profile, 'education[0].program', ''),
          Validators.compose([Validators.maxLength(CHARACTER_LIMIT)]),
        ],
        school: [
          get(this.profile, 'education[0].school', ''),
          Validators.compose([Validators.maxLength(CHARACTER_LIMIT)]),
        ],
        class_of: [
          get(this.profile, 'education[0].class_of', ''),
          Validators.compose([Validators.maxLength(4)]),
        ],
      }),
      experience: this.fb.group({
        job_title: [
          get(this.profile, 'experience[0].job_title', ''),
          Validators.compose([Validators.maxLength(CHARACTER_LIMIT)]),
        ],
        organization_name: [
          get(this.profile, 'experience[0].organization_name', ''),
          Validators.compose([Validators.maxLength(CHARACTER_LIMIT)]),
        ],
      }),
      first_name: this.fb.control(this.profile.first_name, Validators.required),
      geo_location: this.fb.group({
        ...BASE_SUBFORM_CONFIGS.geo_location,
        ...get(this.profile, ['geo_location', '0'], {}),
      }),
      last_name: this.fb.control(this.profile.last_name, Validators.required),
      pronouns: this.fb.control(this.profile.pronouns),
      short_bio: this.fb.control(this.profile.short_bio),
      website: this.fb.control(
        get(this.profile, 'website', ''),
        this.formValidatorService.validUrl,
      ),
    };
    const adminFormControls = {
      email: this.fb.control(this.profile.email, Validators.email),
    };
    const formControls = this.user.isAdmin
      ? {
          ...baseFormControls,
          ...adminFormControls,
        }
      : { ...baseFormControls };
    this.form = this.fb.group(formControls);
    this.initErrorStateForInvalidForms(this.form.controls);
    this.initialValue = this.form.value;
    this.formChangesSubscription = this.form.valueChanges
      .pipe(
        takeUntil(this.componentDestroy),
        tap((formValue: any) => {
          this.formHasChanges = this.hasUnsavedChanges(
            this.initialValue,
            formValue,
          );
          this.store.dispatch(EditedProfile(this.formHasChanges));
        }),
      )
      .subscribe();
    this.filteredLocationOptions =
      this.form.controls.geo_location.valueChanges.pipe(
        debounceTime(200),
        map((formValue: any) => get(formValue, ['address'])),
        startWith(null),
        switchMap((address: string) =>
          this.locationService.getAutocomplete(address),
        ),
      );
  }

  ngAfterViewInit() {
    defer(() => {
      result((this as any)[this.data.focusElement], 'nativeElement.focus');
    });
  }

  ngOnDestroy() {
    this.store.dispatch(EditedProfile(false));
    this.componentDestroy.next();
    this.componentDestroy.complete();
  }

  save() {
    const formValue = this.form.value;
    const update: any = keys(formValue)
      .filter((formFieldName: string) => {
        if (
          some(profileArrayFields, (field: string) =>
            isEqual(formFieldName, field),
          )
        ) {
          return !isEqual(
            formValue[formFieldName],
            get(this.profile, `${formFieldName}[0]`),
          );
        }
        return !isEqual(
          get(formValue, formFieldName),
          get(this.profile, formFieldName),
        );
      })
      .reduce((accumulator: any, fieldName: string) => {
        const profileValue = get(this.profile, fieldName);
        let valueToApply = isArray(profileValue)
          ? tail(profileValue)
          : get(formValue, fieldName);
        const updatedField = get(formValue, fieldName);
        if (
          'experience' === fieldName &&
          (updatedField.job_title || updatedField.organization_name)
        ) {
          valueToApply = [updatedField, ...valueToApply];
        }
        if (
          'education' === fieldName &&
          (updatedField.program || updatedField.school || updatedField.class_of)
        ) {
          valueToApply = [updatedField, ...valueToApply];
        }
        if ('geo_location' === fieldName && updatedField.address) {
          valueToApply = [updatedField, ...valueToApply];
        }
        if ('website' === fieldName) {
          valueToApply =
            (updatedField && sanitizeUrlWithProtocol(updatedField)) || '';
        }
        return set({ ...accumulator }, fieldName, valueToApply);
      }, {});

    if (isEmpty(update)) {
      return;
    }

    this.isLoading = true;

    if (!update.geo_location || !get(update, 'geo_location.0.address')) {
      return this.generateUserMutation(update).subscribe();
    }

    return of(update)
      .pipe(
        map((updateCopy: any) =>
          get(updateCopy, ['geo_location', 0, 'address']),
        ),
        concatMap((address: string) =>
          this.geocoderService.getLocationFromGeocode(address),
        ),
        concatMap((response) => {
          const location = get(response, 'location', []);
          if (!get(location, 'coordinates.length')) {
            const geoLocationFormGroup: UntypedFormGroup = this.form.controls
              .geo_location as UntypedFormGroup;
            geoLocationFormGroup.controls.address.setErrors({
              invalidLocation: true,
            });
            return throwError(
              () => 'Location is not valid location from auto-complete',
            );
          }
          return of(location);
        }),
        map((location: any) => ({
          ...update,
          geo_location: [
            {
              ...update.geo_location[0],
              location,
            },
            ...tail(update.geo_location),
          ],
        })),
        concatMap((updateWithLocation) =>
          this.generateUserMutation(updateWithLocation),
        ),
        catchError(() => {
          this.isLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  generateUserMutation(update: any) {
    const profileCopy = {
      ...this.profile,
    };
    const updatedProfile = {
      ...profileCopy,
      ...update,
    };
    const source = this.user.isAdmin
      ? this.getAdminUserMutation(this.profile, update)
      : this.getNonAdminUserMutation(update);
    return source.pipe(
      tap((response: Account) => {
        const updatedResponse = pick(response, EDITABLE_FIELDS);
        this.dialogRef.close(
          this.updateProfileStore(updatedProfile, updatedResponse),
        );
        this.error = '';
      }),
      catchError((response: any) => {
        this.error = get(response, 'error.message', response.message);
        this.updateProfileStore(profileCopy, {});
        return EMPTY;
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    );
  }

  getNonAdminUserMutation(update: any) {
    const nonAdminUpdate = pick(update, BASE_EDITABLE_FIELDS);
    return this.accountBackend.patchAccount(nonAdminUpdate).pipe(
      tap(() =>
        this.analyticsService.eventTrack('Profile Edit Dialog - Interaction', {
          update,
        }),
      ),
      map((updateResponse: { data: Account }) => updateResponse.data),
    );
  }

  getAdminUserMutation(profile: Account, update: any) {
    const adminEditableFields = [
      ...BASE_EDITABLE_FIELDS,
      ...ADMIN_EDITABLE_FIELDS,
    ];
    const adminUpdate = pick(update, adminEditableFields);
    return !isEmpty(adminUpdate)
      ? this.accountEditService.updateAccount(profile, adminUpdate)
      : EMPTY;
  }

  checkForUnsavedChanges() {
    if (!this.hasUnsavedChanges(this.initialValue, this.form.value)) {
      return this.dialogRef.close();
    }
    const config: any = {
      autoFocus: false,
      maxWidth: '500px',
      width: '95vw',
    };
    this.dialog
      .open(ProfileUnsavedChangesDialogComponent, config)
      .afterClosed()
      .pipe(
        filter((discardChanges: boolean) => discardChanges),
        tap(() => {
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }

  hasUnsavedChanges(initialFormValue: any, formValue: any) {
    return !isEqual(initialFormValue, formValue);
  }

  updateProfileStore(currentProfile: Account, update: { [key: string]: any }) {
    const updatedProfile = {
      ...currentProfile,
      ...update,
    };
    this.store.dispatch(LoadProfile(updatedProfile));
    return updatedProfile;
  }

  initErrorStateForInvalidForms(formControls: {
    [formName: string]: FormGroupControl;
  }): any {
    return forEach(formControls, (formControl: FormGroupControl) => {
      if (formControl.valid) {
        return;
      }
      if (!isEmpty(formControl.controls)) {
        return this.initErrorStateForInvalidForms(get(formControl, 'controls'));
      }
      return formControl.markAsTouched();
    });
  }
}
